import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPageContent, subscribeToPage } from '../firebase/firebase-config';
import '../styles/rich-text.css';

function Curriculum() {
  const [content, setContent] = useState({
    title: 'Curriculum',
    overview: '',
    subjects: '',
    methodology: '',
    assessment: '',
    curriculumFile: null,
    featuredImage: ''
  });
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    primaryColor: '#1a3a6b',
    secondaryColor: '#f59e0b'
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [curriculumData, settingsData] = await Promise.all([
          getPageContent('curriculum'),
          import('../firebase/firebase-config').then(module => module.getSettings())
        ]);
        setContent(prev => ({ ...prev, ...curriculumData }));
        if (settingsData) setSettings(settingsData);
      } catch (error) {
        console.error('Error loading curriculum:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
    const unsubscribe = subscribeToPage('curriculum', (data) => {
      if (data) setContent(prev => ({ ...prev, ...data }));
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Curriculum...</p>
      </div>
    );
  }

  const primaryColor = settings.primaryColor || '#1a3a6b';
  const secondaryColor = settings.secondaryColor || '#f59e0b';

  return (
    <div className="curriculum-page">
      {/* Hero Section */}
      
        <div className="container">
          <h1>{content.title || 'Curriculum'}</h1>
          <div className="hero-divider"></div>
          <p>Comprehensive Academic Framework for Excellence</p>

      </div>

      <div className="container">
        {/* Featured Image */}
        {content.featuredImage && (
          <div className="featured-image">
            <img src={content.featuredImage} alt="Curriculum" />
          </div>
        )}

        {/* Overview Section - Rich Text */}
        {content.overview && (
          <section className="curriculum-section">
            <h2 style={{ color: primaryColor }}>Curriculum Overview</h2>
            <div className="section-divider" style={{ background: secondaryColor }}></div>
            <div 
              className="rich-text-content"
              dangerouslySetInnerHTML={{ __html: content.overview }}
            />
          </section>
        )}

        {/* Subjects Offered Section - Rich Text */}
        {content.subjects && (
          <section className="curriculum-section">
            <h2 style={{ color: primaryColor }}>Subjects Offered</h2>
            <div className="section-divider" style={{ background: secondaryColor }}></div>
            <div 
              className="rich-text-content"
              dangerouslySetInnerHTML={{ __html: content.subjects }}
            />
          </section>
        )}

        {/* Teaching Methodology Section - Rich Text */}
        {content.methodology && (
          <section className="curriculum-section">
            <h2 style={{ color: primaryColor }}>Teaching Methodology</h2>
            <div className="section-divider" style={{ background: secondaryColor }}></div>
            <div 
              className="rich-text-content"
              dangerouslySetInnerHTML={{ __html: content.methodology }}
            />
          </section>
        )}

        {/* Assessment Pattern Section - Rich Text */}
        {content.assessment && (
          <section className="curriculum-section">
            <h2 style={{ color: primaryColor }}>Assessment Pattern</h2>
            <div className="section-divider" style={{ background: secondaryColor }}></div>
            <div 
              className="rich-text-content"
              dangerouslySetInnerHTML={{ __html: content.assessment }}
            />
          </section>
        )}

        {/* Curriculum File Download */}
        {content.curriculumFile && content.curriculumFile.url && (
          <div className="download-section">
            <div className="download-card" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}>
              <div className="download-icon">📄</div>
              <h3>Download Curriculum</h3>
              <p>Get detailed information about our curriculum structure</p>
              <a 
                href={content.curriculumFile.url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="download-btn"
                style={{ background: secondaryColor }}
              >
                Download {content.curriculumFile.name ? content.curriculumFile.name.split('.').pop().toUpperCase() : 'PDF'} →
              </a>
            </div>
          </div>
        )}

        {/* Back to Academics Link */}
        <div className="back-link">
          <Link to="/academics" className="back-btn" style={{ borderColor: primaryColor, color: primaryColor }}>
            ← Back to Academics
          </Link>
        </div>
      </div>

      <style>{`
        .curriculum-page {
          background: #f8fafc;
          min-height: 100vh;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e2e8f0;
          border-top-color: ${primaryColor};
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px 60px;
        }

        /* Page Hero */
        .page-hero {
          color: white;
          text-align: center;
          padding: 60px 20px;
          margin-bottom: 40px;
        }

        .page-hero h1 {
          font-size: 48px;
          margin-bottom: 20px;
          font-weight: 700;
        }

        .hero-divider {
          width: 60px;
          height: 4px;
          background: white;
          margin: 0 auto 20px;
          border-radius: 2px;
        }

        .page-hero p {
          font-size: 18px;
          opacity: 0.9;
        }

        /* Featured Image */
        .featured-image {
          margin-bottom: 40px;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .featured-image img {
          width: 100%;
          height: auto;
          object-fit: cover;
        }

        /* Curriculum Sections */
        .curriculum-section {
          background: white;
          border-radius: 20px;
          padding: 35px;
          margin-bottom: 30px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          transition: transform 0.3s, box-shadow 0.3s;
        }

        .curriculum-section:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
        }

        .curriculum-section h2 {
          font-size: 28px;
          margin-bottom: 15px;
          font-weight: 700;
        }

        .section-divider {
          width: 60px;
          height: 4px;
          margin-bottom: 25px;
          border-radius: 2px;
        }

        /* Download Section */
        .download-section {
          margin: 40px 0;
        }

        .download-card {
          border-radius: 20px;
          padding: 40px;
          text-align: center;
          color: white;
          transition: transform 0.3s;
        }

        .download-card:hover {
          transform: translateY(-5px);
        }

        .download-icon {
          font-size: 60px;
          margin-bottom: 20px;
        }

        .download-card h3 {
          font-size: 24px;
          margin-bottom: 10px;
        }

        .download-card p {
          opacity: 0.9;
          margin-bottom: 25px;
        }

        .download-btn {
          display: inline-block;
          color: white;
          padding: 12px 32px;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s;
        }

        .download-btn:hover {
          background: white !important;
          color: ${primaryColor};
          transform: translateY(-2px);
        }

        /* Back Link */
        .back-link {
          text-align: center;
          margin-top: 40px;
        }

        .back-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: transparent;
          padding: 12px 28px;
          border-radius: 40px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s;
          border: 2px solid;
        }

        .back-btn:hover {
          background: ${primaryColor};
          color: white !important;
          transform: translateX(-5px);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .container {
            padding: 0 20px 50px;
          }
        }

        @media (max-width: 768px) {
          .page-hero {
            padding: 50px 20px;
          }
          
          .page-hero h1 {
            font-size: 32px;
          }
          
          .page-hero p {
            font-size: 16px;
          }
          
          .curriculum-section {
            padding: 25px;
          }
          
          .curriculum-section h2 {
            font-size: 24px;
          }
          
          .download-card {
            padding: 30px 20px;
          }
          
          .download-card h3 {
            font-size: 20px;
          }
        }

        @media (max-width: 480px) {
          .page-hero h1 {
            font-size: 28px;
          }
          
          .curriculum-section {
            padding: 20px;
          }
          
          .curriculum-section h2 {
            font-size: 22px;
          }
          
          .download-icon {
            font-size: 48px;
          }
        }
      `}</style>
    </div>
  );
}

export default Curriculum;